import React from 'react';
import { cn } from '../../../lib/utils';
import { OrkaPriorityChip } from '../OrkaPriorityChip';
import { OrkaStatePill } from '../OrkaStatePill';
import { OrkaText } from '../OrkaText';
import { OrkaTaskMoveHandle } from './OrkaTaskMoveHandle';
import { OrkaTaskMenuButton } from './OrkaTaskMenuButton';

/**
 * OrkaTaskRow — compact one-line task representation (PRD §5.1, §7.3).
 *
 * Used in Backlog, Scheduled lists, and any other dense list surface where
 * a full header would be too heavy. Still respects:
 *   - priority left rail (independent from project)
 *   - supporting/observing left edge accents
 *   - explicit Move handle (six-dot) and Menu button (three-dot)
 *   - state pill, effort badge, work area name
 *
 *   <OrkaTaskRow task={t} onMove={...} onMenu={...} />
 */
const SUPPORTING_BORDER = '#FB923C'; // orange-400, semantic accent
const OBSERVING_BORDER = '#2DD4BF';  // teal-400

export function OrkaTaskRow({
  task,
  density = 'standard',
  movementState = 'idle',
  onClick,
  onMove,
  onMenu,
  onDragStart,
  onDragEnd,
  draggable = true,
  rightSlot,
  className,
  testId,
}) {
  if (!task) return null;
  const title = task.title || task.task_title || 'Untitled';
  const effort = task.estimate || task.task_estimated_effort || 1;
  const workArea = task.work_area_name || task.category;
  const supporting = task._isSupporting;
  const observing = task._isObserved;

  const leftAccent = supporting
    ? SUPPORTING_BORDER
    : observing
      ? OBSERVING_BORDER
      : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); } }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      draggable={draggable && !task.task_hidden}
      data-task-id={task.id || task.task_id}
      data-movement={movementState}
      data-testid={testId}
      className={cn(
        'orka-task-row group relative w-full',
        'orka-surface-base orka-border-subtle border rounded-lg',
        'orka-transition-colors orka-focus-ring',
        'hover:orka-surface-subtle',
        'cursor-grab active:cursor-grabbing',
        movementState === 'dragging' && 'opacity-50',
        task.task_hidden && 'opacity-50 cursor-not-allowed',
        density === 'compact' ? 'py-1' : 'py-1.5',
        className,
      )}
      style={{
        borderTopWidth: 3,
        borderTopColor: task.priority_color || `var(--priority-color, transparent)`,
      }}
    >
      {/* Supporting / observing left edge accent */}
      {leftAccent && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 rounded-l-lg"
          style={{ width: 3, backgroundColor: leftAccent }}
        />
      )}

      <div className={cn('flex items-center gap-2 px-2.5')}>
        {onMove && (
          <OrkaTaskMoveHandle
            onPointerDown={onMove}
            onClick={(e) => e.stopPropagation()}
            size="sm"
          />
        )}

        <div className="flex-1 min-w-0 flex items-center gap-2">
          <OrkaPriorityChip priority={task.task_priority ?? task.priority} variant="chip" size="sm" />
          <OrkaText size="small" variant="primary" truncate className="font-medium">
            {title}
          </OrkaText>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <OrkaStatePill state={task.task_status || task.state} size="sm" />
          <OrkaText size="micro" variant="tertiary" className="font-mono">E:{effort}</OrkaText>
          {workArea && workArea !== 'General' && (
            <OrkaText size="micro" variant="tertiary" truncate className="max-w-[100px]">
              {workArea}
            </OrkaText>
          )}
          {rightSlot}
          {onMenu && (
            <OrkaTaskMenuButton
              onClick={(e) => { e.stopPropagation(); onMenu(e); }}
              size="sm"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default OrkaTaskRow;
